import {TrainingAPI} from "../../api/TrainingAPI";
import { isStaffEmail } from "@utils/user_utils";
import {useDispatch, useSelector} from "react-redux";
import {Badge, Button, Form, Input, Modal, Popconfirm, Row, Table, Tag} from "antd";
import React, {useEffect, useMemo, useState} from "react";
import {DeleteOutlined, EditOutlined, ExclamationCircleOutlined, MailOutlined} from "@ant-design/icons";
import {DeletePopConfirmContent} from "./PlayerOnboarding/PlayerTable";
import {
  addTeamCoach,
  editTeamCoach,
  deleteCoach,
  sendWelcomeEmailToCoach
} from "../../store/actions/team.actions";

import { AdminNotice } from "../Settings/AdminNotice";


import { 
  PermissionedButton,
  useUserPermissions,
} from "../PermissionedComponents";
import {useAuth0} from "@auth0/auth0-react";

import BlurredPreview from "./coach-onboarding.png";
import { PremiumFeatureDisabled } from "../Common/PremiumFeatureDisabled";

const MODAL_MODE_EDIT = "edit";
const MODAL_MODE_CREATE = "create";

const MODAL_TITLES = {
  [MODAL_MODE_CREATE]: "Add Coach",
  [MODAL_MODE_EDIT]: "Edit Coach",
};


const CoachForm = ({
  defaultValue = {},
  onSubmit = () => {},
  onCancel = () => {},
}) => {
  const [form] = Form.useForm();
  const {
    info: {
      coach_list: coachList,
      add_coach_config: addCoachConfig
    }
  } = useSelector(({ team }) => team);

    const { user } = useAuth0();
    const isStaff = isStaffEmail(user?.email);

  const edit_mode = defaultValue?.email ? true : false;

  const [_, setPending] = useState(false);

  const handleSubmit = async () => {
    setPending(true);
    try {
      const values = await form.validateFields();
      await onSubmit({ ...defaultValue, ...values });
      handleCancel();
    } catch (e) {
      console.log(e);
      return;
    }
    setPending(false);
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  useEffect(() => {
    form.resetFields(["first_name", "Hello"]);
  }, []);

  const checkFullName = (getFieldValue) => {
    const firstName = getFieldValue("first_name")?.trim() || "";
    const lastName = getFieldValue("last_name")?.trim() || "";
    const foundCoaches = coachList.filter(
      ({ first_name, last_name, email }) => {
        return (
          first_name === firstName &&
          last_name === lastName &&
          email !== defaultValue?.email
        );
      }
    );
    return foundCoaches.length === 0;
  };

  const checkEmail = (getFieldValue) => {
    const email = getFieldValue("email")?.trim() || "";
    const foundCoaches = coachList.filter(
      ({ email: coachEmail, player_id }) => {
        return coachEmail === email && player_id !== defaultValue?.player_id;
      }
    );
    return foundCoaches.length === 0;
  };

  const nameValidator = ({ getFieldValue }) => ({
    validator(_, value) {
      if (checkFullName(getFieldValue)) {
        return Promise.resolve();
      }
      return Promise.reject(new Error("Same name already exists"));
    },
  });

  const emailValidator = ({ getFieldValue }) => ({
    validator(_, value) {
      if (edit_mode) return Promise.resolve();
      if (checkEmail(getFieldValue)) {
        return Promise.resolve();
      }
      return Promise.reject(new Error("Same email already exists"));
    },
  });

  const domainValidator = ({ getFieldValue }) => ({
    validator(_, value) {
      if (isStaff) return Promise.resolve();
      if (edit_mode) return Promise.resolve();
      const valid_domains = addCoachConfig || [];
      // loop through valid domains and check if email ends with any of them
      for (let domain of valid_domains) {
        if (getFieldValue("email")?.endsWith(domain)) {
          return Promise.resolve();
        }
      }
      return Promise.reject(new Error("Domain not allowed"));
    },
  });

  useEffect(() => {
    if (defaultValue === null) form.resetFields();
    else {
      form.setFieldsValue(defaultValue);
      form.validateFields();
    }
  }, [defaultValue]);

  return (
    <Form
      form={form}
      name="player-form"
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 18 }}
    >
      <Form.Item
        label="First Name"
        name="first_name"
        rules={[
          { required: true, message: "Please input first name" },
          nameValidator,
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Last Name"
        name="last_name"
        rules={[
          { required: true, message: "Please input last name" },
          nameValidator,
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Email"
        name="email"
        extra={<>Valid email domains: {(addCoachConfig || []).join(", ")} </>}
        rules={[
          { required: true, type: "email", message: "Not a valid email" },
          emailValidator,
          domainValidator
        ]}
      >
        <Input disabled={defaultValue?.email ? true : false} />
      </Form.Item>
      <Form.Item wrapperCol={{ offset: 16, span: 8 }}>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          type="primary"
          style={{ margin: "0 8px" }}
        >
          Save
        </Button>
      </Form.Item>
    </Form>
  );
}


export const CoachDialog = ({
  open,
  onCancel,
  selectedCoach = null,
  onSubmit,
}) => {
  const mode = useMemo(
    () => (selectedCoach ? MODAL_MODE_EDIT : MODAL_MODE_CREATE),
    [selectedCoach]
  );

  const modalTitle = useMemo(() => MODAL_TITLES[mode], [mode]);

  return (
    <Modal
      open={open}
      title={modalTitle}
      onCancel={onCancel}
      footer={null}
    >
      <CoachForm
        defaultValue={selectedCoach}
        onSubmit={onSubmit}
        onCancel={onCancel}
      />
    </Modal>
  );
};


const DeleteCoachPopConfirmContent = () => {
  return (
    <div style={{ width: 256 }}>
      This is a permanent action. <br />
      You are about to delete this coach's data and login credentials.
      <br />
    </div>
  );
};


export const CoachOnboarding = () => {
  const trainingAPI = new TrainingAPI();
  const dispatch = useDispatch();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);

  const userPermissions = useUserPermissions();
  const {
    info: {
      coach_list: coachList,
      add_coach_enabled: addCoachEnabled,
      id: teamId,
      permissions_manager_list: permissionsManagerList
    }
  } = useSelector(({ team }) => team);

    const { user } = useAuth0();
    const isStaff = isStaffEmail(user?.email);
    const hasManageCoachLoginsPermission = userPermissions?.ManageCoachLogins || false;

    if (!addCoachEnabled && !isStaff){
    return (
      <PremiumFeatureDisabled
        bg_image={BlurredPreview}
        show_navbar={false}
        feature_name={"Coach Management"}
        info_text={<>
        This is an elite feature that will allow manage your Coach roster. <br/> <br/> 
        If you are interested in adding this to your platform, please contact your sales rep!</>}
      />
    )
  }

  if (addCoachEnabled && !hasManageCoachLoginsPermission) {
    return (
      <div>
        You do not have permissions to view this page. <br/> 
        If you want to add/remove specific permissions to your account, please reach out to your team's permissions manager. <br/><br/>
        Your team's Permissions Manager(s): <strong>{permissionsManagerList.map(user => `${user.first_name} ${user.last_name}`).join(", ")}</strong>
      </div>
    )
  }

  const handleOpenAddModal = () => {
    setCreateModalOpen(true);
  };

  const handleOpenEditModal = (coach) => () => {
    setSelectedCoach(coach);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setSelectedCoach(null);
    setEditModalOpen(false);
  };

  const handleCloseModal = () => {
    setCreateModalOpen(false);
    setEditModalOpen(false);
  };

  const addCoach = (newCoach) => {
    dispatch(
      addTeamCoach({
        ...newCoach,
        team_id: teamId
      })
    );
  };

  const editCoach = (coachData) => {
    dispatch(
      editTeamCoach({
        ...coachData,
        team_id: teamId
      })
    );
    console.log("Editing: ", coachData);
  };

  const handleDeleteCoach = async (record) => {
    dispatch(
      deleteCoach(record.email)
    );
  }

  const handleSendWelcomeEmail = (record) => async () => {
    dispatch(
      sendWelcomeEmailToCoach(record.email)
    );
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: {
        compare: (a, b) => a.last_name.localeCompare(b.last_name),
        multiple: 1,
      },
      render: (name, data) => {
        return (
          <>
            {data.first_name} {data.last_name}
          </>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: {
        compare: (a, b) => a.email.localeCompare(b.email),
        multiple: 3,
      },
      render: (email, data) => {
        return (
          <>
            {data.email}
          </>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          <Badge
            offset={[-5, 5]}
          >
            <Button
              icon={<MailOutlined />}
              shape="circle"
              onClick={handleSendWelcomeEmail(record)}
              disabled={!record.email}
            />
          </Badge>
            <Button
              icon={<EditOutlined />}
              shape="circle"
              onClick={() => {
                setSelectedCoach(record);
                setEditModalOpen(true);
              }}
              disabled={!hasManageCoachLoginsPermission}

            />
          <Popconfirm
            placement="right"
            title={<DeleteCoachPopConfirmContent />}
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDeleteCoach(record)}
            icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
            disabled={!hasManageCoachLoginsPermission}
          >
          <PermissionedButton 
            icon={<DeleteOutlined />} shape="circle" danger ghost
            permissionCheck="ManageCoachLogins"
        /> 
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
        {
          (!addCoachEnabled && isStaff) ? 
            <AdminNotice />
           : null
        }
      <Row style={{ margin: "0px 0px 12px"}}>
        <PermissionedButton 
            type={"primary"}
            onClick={handleOpenAddModal}
            permissionCheck="ManageCoachLogins"
        > Add Coach </PermissionedButton>
      </Row>
      <Table
        rowKey="rowId"
        columns={columns}
        size="small"
        dataSource={coachList}
        style={{ width: '50vw', maxWidth: "100%" }}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30"],
          position: ["topLeft"],
        }}
      />
      <CoachDialog
        open={createModalOpen}
        onCancel={handleCloseModal}
        onSubmit={addCoach}
        selectedCoach={null}
        // disabled={!hasRosterAccess}
      />
      <CoachDialog
        open={editModalOpen}
        onCancel={handleCloseEditModal}
        onSubmit={editCoach}
        selectedCoach={selectedCoach}
        // disabled={!hasRosterAccess}
      />
      {/*<CoachDialog*/}
      {/*  open={editModalOpen}*/}
      {/*  onCancel={handleCloseModal}*/}
      {/*  onSubmit={editCoach}*/}
      {/*  selectedCoach={selectedCoach}*/}
      {/*/>*/}
    </>
  )
};
